import React, { useState, useEffect } from 'react';
import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';
import { billingService } from '../../services/billingService';
import { customerService } from '../../services/customerService';
import { paymentService } from '../../services/paymentService';
import { supabase } from '../../supabaseClient';
import ResponsiveDataView from '../common/ResponsiveDataView';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Alert,
  CircularProgress,
  Stack,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  InputAdornment,
  Divider,
  IconButton
} from '@mui/material';
import {
  Search as SearchIcon,
  Print as PrintIcon,
  Warning as WarningIcon,
  Close as CloseIcon,
  FilterList as FilterListIcon,
  Refresh as RefreshIcon,
  Download as DownloadIcon
} from '@mui/icons-material';

const NoticePage = () => {
  // State
  const [customersWithUnpaidBills, setCustomersWithUnpaidBills] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [excludedCustomerIds, setExcludedCustomerIds] = useState(new Set());
  
  // Filters
  const [filterBarangay, setFilterBarangay] = useState('all');
  const [filterType, setFilterType] = useState(['Commercial', 'Residential']);
  const [filterStatus, setFilterStatus] = useState('Active');
  const [filterMonth, setFilterMonth] = useState(''); // YYYY-MM
  
  // Options
  const [barangays, setBarangays] = useState([]);
  const [customerTypes, setCustomerTypes] = useState([]);
  const [users, setUsers] = useState([]);
  
  // Settings & Configuration
  const [surchargeSettings, setSurchargeSettings] = useState(null);
  const [mayorLogo, setMayorLogo] = useState('/logo-left.png');
  const [treasurerLogo, setTreasurerLogo] = useState('/logo-right.png');
  
  // Dialog State
  const [showNoticeDialog, setShowNoticeDialog] = useState(false);
  const [disconnectionDate, setDisconnectionDate] = useState('');
  const [reconnectionFee, setReconnectionFee] = useState('200');
  const [selectedAuthority, setSelectedAuthority] = useState('');
  const [warningMessage, setWarningMessage] = useState(
    'This is to inform you that your water service account has OVERDUE BILLS that remain unpaid. If payment is not received on or before {DATE}, your water service will be DISCONNECTED without further notice. Reconnection will require payment of all outstanding balances plus applicable reconnection fees which is {FEE}.'
  );

  // Initial Load
  useEffect(() => {
    loadFilterOptions();
    loadUsers();
    loadDepartmentLogos();
    loadSurchargeSettings();
    loadExcludedCustomers();
  }, []);

  // Load Bills when settings are ready or filters change
  // Note: We don't auto-reload on every filter change to avoid too many requests, 
  // but for month filter it might be good.
  // For now, let's load once surcharge settings are ready, and then user can refresh.
  useEffect(() => {
    if (surchargeSettings) {
      loadCustomersWithUnpaidBills();
    }
  }, [surchargeSettings, filterMonth]); // Reload when month changes

  useEffect(() => {
    if (surchargeSettings) {
      loadCustomersWithUnpaidBills();
    }
  }, [excludedCustomerIds]);

  const loadExcludedCustomers = async () => {
    try {
      const { data, error } = await supabase
        .from('notice_excluded_customers')
        .select('customerid');

      if (error) {
        setExcludedCustomerIds(new Set());
        return;
      }

      const ids = new Set((data || []).map(r => String(r.customerid)));
      setExcludedCustomerIds(ids);
    } catch (err) {
      console.error('Error loading notice exclusions:', err);
      setExcludedCustomerIds(new Set());
    }
  };

  const loadSurchargeSettings = async () => {
    const result = await paymentService.getSurchargeSettings();
    if (result.success) {
      setSurchargeSettings(result.data);
    }
  };

  const loadUsers = async () => {
    try {
      const { data, error } = await supabase
        .from('users')
        .select('userid, firstname, lastname, position, status, signature_url')
        .eq('status', 'active')
        .order('lastname');

      if (error) throw error;

      if (data) {
        setUsers(data);
        const treasurer = data.find(u => u.position?.toLowerCase().includes('municipal treasurer'));
        if (treasurer) {
          setSelectedAuthority(treasurer.userid);
        } else if (data.length > 0) {
           setSelectedAuthority(data[0].userid);
        }
      }
    } catch (err) {
      console.error('Error loading users:', err);
    }
  };

  const loadDepartmentLogos = async () => {
    try {
      const { data, error } = await supabase
        .from('lgu_departments')
        .select('department_name, department_code, logo_url');

      if (error) throw error;

      if (data) {
        const mayorDept = data.find(d => {
          const name = d.department_name?.toLowerCase() || '';
          const code = d.department_code?.toUpperCase() || '';
          return code === 'MAYOR' || name.includes('mayor');
        });
        if (mayorDept?.logo_url) setMayorLogo(mayorDept.logo_url);

        const treasurerDept = data.find(d => {
          const name = d.department_name?.toLowerCase() || '';
          const code = d.department_code?.toUpperCase() || '';
          return code === 'TREASURER' || code === 'MTO' || name.includes('treasurer');
        });
        if (treasurerDept?.logo_url) setTreasurerLogo(treasurerDept.logo_url);
      }
    } catch (err) {
      console.error('Error loading department logos:', err);
    }
  };
  
  const loadFilterOptions = async () => {
    try {
      const [barangayResult, typeResult] = await Promise.all([
        customerService.getBarangays(),
        customerService.getCustomerTypes()
      ]);
      
      if (barangayResult.success) setBarangays(barangayResult.data || []);
      if (typeResult.success) setCustomerTypes(typeResult.data || []);
    } catch (err) {
      console.error('Error loading filter options:', err);
    }
  };
  
  const loadCustomersWithUnpaidBills = async () => {
    setLoading(true);
    try {
      // Pass month filter to service
      const result = await billingService.getCustomersWithUnpaidBills({
        monthFilter: filterMonth
      });
      
      if (result.success && surchargeSettings) {
        const customersWithCalculatedSurcharge = (result.data || [])
          .filter(c => !excludedCustomerIds.has(String(c.customerid)))
          .map(customer => {
          const bills = customer.unpaidBills || [];
          let totalBasicAmount = 0;
          let totalCalculatedSurcharge = 0;
          
          const billsWithSurcharge = bills.map(bill => {
            const basicAmount = parseFloat(bill.basicamount || 0);
            const surchargeInfo = paymentService.calculateSurcharge(bill, surchargeSettings);
            
            totalBasicAmount += basicAmount;
            totalCalculatedSurcharge += surchargeInfo.surchargeAmount;

            return {
                ...bill,
                calculatedSurcharge: surchargeInfo.surchargeAmount,
                totalAmount: basicAmount + surchargeInfo.surchargeAmount
            };
          });
          
          return {
            ...customer,
            unpaidBills: billsWithSurcharge,
            totalBasicAmount,
            totalSurcharge: totalCalculatedSurcharge,
            totalBilledAmount: totalBasicAmount + totalCalculatedSurcharge
          };
        });
        
        setCustomersWithUnpaidBills(customersWithCalculatedSurcharge);
      } else if (result.success) {
        setCustomersWithUnpaidBills((result.data || []).filter(c => !excludedCustomerIds.has(String(c.customerid))));
      }
    } catch (err) {
      console.error('Error loading customers with unpaid bills:', err);
    }
    setLoading(false);
  };

  const filteredNoticeData = customersWithUnpaidBills.filter(customer => {
    const matchesSearch = !searchTerm || 
      customer.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      String(customer.customerid).includes(searchTerm) ||
      customer.barangay?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      customer.address?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      String(customer.meterno || '').includes(searchTerm);
    
    const matchesBarangay = filterBarangay === 'all' || 
      customer.barangay?.toLowerCase() === filterBarangay.toLowerCase();
    const matchesType = filterType.length === 0 || 
      filterType.some(t => t.toLowerCase() === customer.type?.toLowerCase());
    const matchesStatus = filterStatus === 'all' || 
      customer.status?.toLowerCase() === filterStatus.toLowerCase();
    
    return matchesSearch && matchesBarangay && matchesType && matchesStatus;
  });

  const getCoveredMonthsRange = () => {
    if (filteredNoticeData.length === 0) return 'N/A';
    
    let minMonth = filteredNoticeData[0].earliestMonth;
    let maxMonth = filteredNoticeData[0].latestMonth;
    
    filteredNoticeData.forEach(customer => {
      if (customer.earliestMonth < minMonth) minMonth = customer.earliestMonth;
      if (customer.latestMonth > maxMonth) maxMonth = customer.latestMonth;
    });
    
    if (!minMonth) return 'N/A';
    return formatBillingPeriod(minMonth === maxMonth ? minMonth : `${minMonth} to ${maxMonth}`);
  };


  const handleExportExcel = async () => {
    let exportData = filteredNoticeData;
    try {
      const { data, error } = await supabase
        .from('notice_excluded_customers')
        .select('customerid');

      if (!error && Array.isArray(data) && data.length > 0) {
        const excludeSet = new Set(data.map(r => String(r.customerid)));
        exportData = filteredNoticeData.filter(c => !excludeSet.has(String(c.customerid)));
      }
    } catch (e) {
    }

    if (exportData.length === 0) {
      alert('No customers to export.');
      return;
    }

    const getPeriodFromTo = (billScope) => {
      if (!billScope) return { from: '', to: '' };

      const formatDate = (dateStr) => {
        if (!dateStr) return '';
        let date;
        if (dateStr.includes('-')) {
          const parts = dateStr.split('-');
          if (parts.length === 2) {
            date = new Date(dateStr + '-01');
          } else {
            date = new Date(dateStr);
          }
        } else {
          date = new Date(dateStr);
        }
        if (isNaN(date.getTime())) return dateStr;
        return date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
      };

      if (billScope.includes(' to ')) {
        const [start, end] = billScope.split(' to ');
        const from = formatDate(start.trim());
        const to = formatDate(end.trim());
        return { from, to };
      } else {
        const single = formatDate(billScope);
        return { from: single, to: single };
      }
    };

    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet('Disconnection Notices');

    worksheet.columns = [
      { header: 'Customer ID', key: 'customerid', width: 12 },
      { header: 'Name', key: 'name', width: 30 },
      { header: 'Barangay', key: 'barangay', width: 18 },
      { header: 'Type', key: 'type', width: 14 },
      { header: 'Unpaid Bills', key: 'billCount', width: 14 },
      { header: 'Period From', key: 'periodFrom', width: 18 },
      { header: 'Period To', key: 'periodTo', width: 18 },
      { header: 'Basic', key: 'basic', width: 14 },
      { header: 'Surcharge', key: 'surcharge', width: 14 },
      { header: 'Total Due', key: 'totalDue', width: 16 }
    ];

    exportData.forEach((customer) => {
      const period = getPeriodFromTo(customer.billScope);
      worksheet.addRow({
        customerid: customer.customerid,
        name: customer.name,
        barangay: customer.barangay,
        type: customer.type,
        billCount: customer.billCount,
        periodFrom: period.from,
        periodTo: period.to,
        basic: customer.totalBasicAmount ?? null,
        surcharge: customer.totalSurcharge ?? null,
        totalDue: customer.totalBilledAmount ?? null
      });
    });

    worksheet.getRow(1).font = { bold: true };

    const lastRow = worksheet.lastRow.number;
    for (let r = 2; r <= lastRow; r++) {
      const row = worksheet.getRow(r);
      row.getCell('basic').numFmt = '#,##0.00';
      row.getCell('surcharge').numFmt = '#,##0.00';
      row.getCell('totalDue').numFmt = '#,##0.00';
    }

    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    const monthPart = filterMonth || 'all';
    const filename = `disconnection_notices_${monthPart}_${new Date().getTime()}.xlsx`;
    saveAs(blob, filename);
  };

  const handleGenerateClick = () => {
    if (filteredNoticeData.length === 0) {
      alert('No customers to generate notices for.');
      return;
    }
    setShowNoticeDialog(true);
  };

  const generateDisconnectionNotices = (mode = 'double') => {
    if (!disconnectionDate) {
      alert('Please enter a disconnection date.');
      return;
    }

    const authority = users.find(u => u.userid === selectedAuthority);
    const authorityName = authority ? `${authority.firstname} ${authority.lastname}` : 'Menard Herrera';
    const authoritySignature = authority?.signature_url;

    setShowNoticeDialog(false);

    const printContent = filteredNoticeData.map((customer, index) => {
      const isEvenRow = index % 2 === 0;
      const baseStyle = 'padding: 0.75rem; font-family: Arial, sans-serif; font-size: 12px; box-sizing: border-box;';
      let stylePrefix = '';
      let heightPart = '';
      let marginPart = '';
      let borderPart = '';

      if (mode === 'double') {
        stylePrefix = (index + 1) % 2 === 0 ? 'page-break-after: always;' : '';
        heightPart = `height: ${isEvenRow ? '6.75in' : '5.5in'};`;
        marginPart = isEvenRow ? '' : 'margin-top: 0.25in;';
        borderPart = isEvenRow ? 'border-bottom: 2px dashed #999;' : '';
      } else {
        stylePrefix = 'page-break-after: always;';
      }

      const style = `${stylePrefix} ${baseStyle} ${heightPart} ${marginPart} ${borderPart}`;

      return `
      <div class="notice-box" style="${style}">
        <div style="position: relative; text-align: center; margin-bottom: 0.6rem; min-height: 70px; display: flex; align-items: center; justify-content: center;">
          <img src="${mayorLogo}" alt="Mayor Logo" style="position: absolute; left: 10px; top: 0; height: 70px; width: auto;" />
          <div>
            <p style="margin: 0; font-size: 11px; line-height: 1.4;">Republic of the Philippines</p>
            <p style="margin: 0; font-size: 11px; line-height: 1.4;">Province of Romblon</p>
            <p style="margin: 0; font-size: 11px; line-height: 1.4;">Municipality of Concepcion</p>
            <p style="margin: 0.4rem 0 0 0; font-size: 12px; font-weight: bold; line-height: 1.4;">OFFICE OF THE MUNICIPAL TREASURER</p>
          </div>
          <img src="${treasurerLogo}" alt="Treasurer Logo" style="position: absolute; right: 10px; top: 0; height: 70px; width: auto;" />
        </div>
        
        <div style="text-align: center; margin-bottom: 0.75rem; border-bottom: 3px solid #dc2626; padding-bottom: 0.5rem;">
          <h1 style="color: #dc2626; margin: 0; font-size: 18px; font-weight: bold;">⚠️ NOTICE OF WATER DISCONNECTION ⚠️</h1>
        </div>

        <div style="margin-bottom: 0.65rem; font-size: 11px;">
          <p style="margin: 0;"><strong>Date:</strong> ${new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}</p>
        </div>

        <div style="display: flex; gap: 0.6rem; margin-bottom: 0.65rem;">
          <div style="flex: 1; background: #fef2f2; border-left: 3px solid #dc2626; padding: 0.6rem;">
            <h3 style="margin: 0 0 0.5rem 0; color: #991b1b; font-size: 13px; font-weight: bold;">Customer Information</h3>
            <table style="width: 100%; border-collapse: collapse; font-size: 12px;">
              <tr>
                <td style="padding: 0.3rem 0; width: 40%;"><strong>ID:</strong></td>
                <td style="padding: 0.3rem 0;">${customer.customerid}</td>
              </tr>
              <tr>
                <td style="padding: 0.3rem 0;"><strong>Name:</strong></td>
                <td style="padding: 0.3rem 0;">${customer.name}</td>
              </tr>
              <tr>
                <td style="padding: 0.3rem 0;"><strong>Address:</strong></td>
                <td style="padding: 0.3rem 0;">${customer.address || ''}</td>
              </tr>
              <tr>
                <td style="padding: 0.3rem 0;"><strong>Barangay:</strong></td>
                <td style="padding: 0.3rem 0;">${customer.barangay}</td>
              </tr>
              <tr>
                <td style="padding: 0.3rem 0;"><strong>Type:</strong></td>
                <td style="padding: 0.3rem 0;">${customer.type}</td>
              </tr>
            </table>
          </div>

          <div style="flex: 1;">
            <h3 style="color: #991b1b; margin: 0 0 0.5rem 0; font-size: 13px; font-weight: bold;">Outstanding Balance</h3>
            <table style="width: 100%; border-collapse: collapse; border: 1px solid #999; font-size: 12px;">
              <tbody>
                <tr style="background: #f9fafb;">
                  <td style="padding: 0.45rem; border: 1px solid #999;">Unpaid Bills</td>
                  <td style="padding: 0.45rem; text-align: right; border: 1px solid #999; font-weight: bold;">${customer.billCount}</td>
                </tr>
                <tr>
                  <td style="padding: 0.45rem; border: 1px solid #999; font-size: 11px;">Period</td>
                  <td style="padding: 0.45rem; text-align: right; border: 1px solid #999; font-size: 11px;">${formatBillingPeriod(customer.billScope)}</td>
                </tr>
                <tr style="background: #f9fafb;">
                  <td style="padding: 0.45rem; border: 1px solid #999;">Basic</td>
                  <td style="padding: 0.45rem; text-align: right; border: 1px solid #999;">₱${customer.totalBasicAmount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                </tr>
                <tr>
                  <td style="padding: 0.45rem; border: 1px solid #999;">Surcharge</td>
                  <td style="padding: 0.45rem; text-align: right; border: 1px solid #999;">₱${customer.totalSurcharge.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                </tr>
                <tr style="background: #fee2e2; font-weight: bold;">
                  <td style="padding: 0.5rem; border: 1px solid #999; font-size: 12px;">TOTAL DUE</td>
                  <td style="padding: 0.5rem; text-align: right; border: 1px solid #999; color: #dc2626; font-size: 13px;">₱${customer.totalBilledAmount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <div style="background: #fef2f2; border: 2px solid #dc2626; padding: 0.65rem; margin-bottom: 0.65rem;">
          <h3 style="color: #991b1b; margin: 0 0 0.4rem 0; font-size: 12px; font-weight: bold;">⚠️ DISCONNECTION WARNING</h3>
          <ul style="line-height: 1.5; margin: 0.2rem 0; padding-left: 1.3rem; font-size: 11px;">
            ${warningMessage
              .replace('{DATE}', `<strong style="color: #dc2626;">${new Date(disconnectionDate).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}</strong>`)
              .replace('{FEE}', `<strong>₱${parseFloat(reconnectionFee).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</strong>`)
              .split(/\.(?=\s|$)/)
              .filter(sentence => sentence.trim())
              .map(sentence => `<li style="margin-bottom: 0.15rem;">${sentence.trim()}.</li>`)
              .join('')
            }
          </ul>
        </div>

        <div style="display: flex; gap: 1rem; margin-top: 0.5rem; align-items: flex-start;">
          <div style="flex: 1;">
            <div style="margin-bottom: 0.5rem;">
              <h4 style="margin: 0 0 0.3rem 0; font-size: 12px; font-weight: bold;">Payment Instructions:</h4>
              <ul style="line-height: 1.4; margin: 0; padding-left: 1.3rem; font-size: 11px;">
                <li style="margin-bottom: 0.15rem;">Please settle your account immediately to avoid service interruption</li>
                <li style="margin-bottom: 0.15rem;">Payment can be made at our office during business hours</li>
                <li style="margin-bottom: 0.15rem;">If you have questions or corrections on your bill, please contact our office so we can update your records in the system</li>
                <li>For other inquiries, please contact our office or visit during office hours</li>
              </ul>
            </div>
            <p style="margin: 0 0 0.25rem 0; font-size: 10px; color: #374151; font-style: italic;">
              If payment has already been made, please disregard this notice.
            </p>
            <p style="margin: 0; font-size: 10px; color: #6b7280; font-style: italic;">This is an official notice. Please keep for your records.</p>
          </div>

          <div style="flex: 0 0 200px; text-align: center;">
            <p style="margin: 0 0 0.1rem 0; font-size: 11px;">By Authority of:</p>
            <div style="margin: 0.2rem auto; width: 150px; position: relative; height: 50px;">
              ${authoritySignature 
                ? `<img src="${authoritySignature}" style="max-height: 50px; max-width: 150px;" alt="Signature" />`
                : `<svg width="150" height="50" viewBox="0 0 200 60" xmlns="http://www.w3.org/2000/svg" style="display: block; margin: 0 auto;">
                    <g>
                      <title>Signature</title>
                      <text x="20" y="45" font-family="'Brush Script MT', 'Cursive', sans-serif" font-size="24" fill="#000080">${authorityName}</text>
                    </g>
                  </svg>`
              }
            </div>
            <p style="margin: 0; font-size: 12px; font-weight: bold;">${authorityName}</p>
            <p style="margin: 0; font-size: 10px;">${authority?.position || 'Municipal Treasurer'}</p>
          </div>
        </div>
      </div>
    `;
    }).join('');

    const mediaNoticeBoxHeight =
      mode === 'double' ? '.notice-box { height: 6.25in; }' : '';

    const printWindow = window.open('', '_blank');
    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <title>Water Disconnection Notices - ${new Date().toLocaleDateString()}</title>
          <base href="${window.location.origin}/">
          <meta charset="UTF-8">
          <style>
            * { box-sizing: border-box; }
            body { margin: 0; padding: 0; }
            .notice-box { width: 100%; box-sizing: border-box; }
            @page { size: legal portrait; margin: 0.25in 0; }
            @media print { body { margin: 0; padding: 0; } ${mediaNoticeBoxHeight} }
          </style>
        </head>
        <body>
          ${printContent}
          <script>window.onload = function() { window.print(); }</script>
        </body>
      </html>
    `);
    printWindow.document.close();
  };

  const formatBillingPeriod = (billScope) => {
    const formatDate = (dateStr) => {
      if (!dateStr) return '';
      let date;
      if (dateStr.includes('-')) {
        const parts = dateStr.split('-');
        if (parts.length === 2) {
          date = new Date(dateStr + '-01');
        } else {
          date = new Date(dateStr);
        }
      } else {
        date = new Date(dateStr);
      }
      if (isNaN(date.getTime())) return dateStr;
      return date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
    };
    
    if (billScope && billScope.includes(' to ')) {
      const [start, end] = billScope.split(' to ');
      const startFormatted = formatDate(start.trim());
      const endFormatted = formatDate(end.trim());
      return startFormatted === endFormatted ? startFormatted : `${startFormatted} to ${endFormatted}`;
    } else {
      return formatDate(billScope);
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5" fontWeight="bold" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <WarningIcon color="error" /> Disconnection Notices
        </Typography>
        <Stack direction="row" spacing={2}>
          <Button 
            variant="outlined" 
            startIcon={<DownloadIcon />}
            onClick={handleExportExcel}
            disabled={loading || filteredNoticeData.length === 0}
          >
            Export Excel
          </Button>
           <Button 
            variant="outlined" 
            startIcon={<RefreshIcon />}
            onClick={() => loadCustomersWithUnpaidBills()}
          >
            Refresh Data
          </Button>
          <Button 
            variant="contained" 
            startIcon={<PrintIcon />}
            onClick={handleGenerateClick}
            disabled={loading || filteredNoticeData.length === 0}
            color="error"
          >
            Generate Notices
          </Button>
        </Stack>
      </Box>

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={3}>
            <TextField
              fullWidth
              placeholder="Search customer..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: <InputAdornment position="start"><SearchIcon /></InputAdornment>,
              }}
              size="small"
            />
          </Grid>
          <Grid item xs={12} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Barangay</InputLabel>
              <Select
                value={filterBarangay}
                label="Barangay"
                onChange={(e) => setFilterBarangay(e.target.value)}
              >
                <MenuItem value="all">All Barangays</MenuItem>
                {barangays.map((bg) => (
                  <MenuItem key={bg.barangay} value={bg.barangay}>{bg.barangay}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Type</InputLabel>
              <Select
                multiple
                value={filterType}
                label="Type"
                onChange={(e) => {
                  const { value } = e.target;
                  setFilterType(
                    // On autofill we get a stringified value.
                    typeof value === 'string' ? value.split(',') : value,
                  );
                }}
                renderValue={(selected) => selected.length === 0 ? 'All Types' : selected.join(', ')}
              >
                {customerTypes.map((t) => (
                  <MenuItem key={t.type} value={t.type}>{t.type}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Status</InputLabel>
              <Select
                value={filterStatus}
                label="Status"
                onChange={(e) => setFilterStatus(e.target.value)}
              >
                <MenuItem value="all">All Statuses</MenuItem>
                <MenuItem value="Active">Active</MenuItem>
                <MenuItem value="Disconnected">Disconnected</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={3}>
             <TextField
                fullWidth
                label="Filter by Month"
                type="month"
                value={filterMonth}
                onChange={(e) => setFilterMonth(e.target.value)}
                InputLabelProps={{
                  shrink: true,
                }}
                size="small"
                helperText={filterMonth ? "Generating notices for this month" : "Showing all overdue"}
              />
          </Grid>
        </Grid>
      </Paper>

      {/* Stats Summary */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>Total Customers</Typography>
              <Typography variant="h4">{filteredNoticeData.length}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>Total Amount Due</Typography>
              <Typography variant="h4" color="error">
                ₱{filteredNoticeData.reduce((sum, c) => sum + c.totalBilledAmount, 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={6}>
            <Card sx={{ bgcolor: '#f5f5f5' }}>
                <CardContent>
                    <Typography variant="subtitle2" fontWeight="bold">Notice Scope:</Typography>
                    <Typography variant="body2" sx={{ mt: 1 }}>
                        {filterMonth 
                            ? `Generating notices specifically for customers with unpaid bills in ${new Date(filterMonth).toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}.` 
                            : "Generating notices for ALL customers with overdue bills."}
                    </Typography>
                    <Typography variant="body2" sx={{ mt: 1, fontStyle: 'italic' }}>
                        * Notices always reflect the total outstanding balance.
                    </Typography>
                </CardContent>
            </Card>
        </Grid>
      </Grid>

      {/* Responsive Demand Notices Data View */}
      <ResponsiveDataView
        columns={[
          {
            key: 'customer',
            label: 'Customer',
            priority: 'high',
            render: (customer) => (
              <Box>
                <Typography variant="body2" fontWeight="bold">{customer.name}</Typography>
                <Typography variant="caption" color="textSecondary">ID: {customer.customerid}</Typography>
              </Box>
            )
          },
          {
            key: 'location',
            label: 'Location',
            priority: 'medium',
            render: (customer) => (
              <Box>
                <Typography variant="body2">{customer.barangay}</Typography>
                <Typography variant="caption" color="textSecondary">{customer.type}</Typography>
              </Box>
            )
          },
          {
            key: 'unpaid',
            label: 'Unpaid Bills',
            priority: 'high',
            render: (customer) => <Chip label={customer.billCount} color="error" size="small" />
          },
          {
            key: 'period',
            label: 'Period',
            priority: 'medium',
            render: (customer) => formatBillingPeriod(customer.billScope)
          },
          {
            key: 'basic',
            label: 'Basic',
            priority: 'low',
            align: 'right',
            render: (customer) => `₱${customer.totalBasicAmount.toFixed(2)}`
          },
          {
            key: 'surcharge',
            label: 'Surcharge',
            priority: 'low',
            align: 'right',
            render: (customer) => `₱${customer.totalSurcharge.toFixed(2)}`
          },
          {
            key: 'totalDue',
            label: 'Total Due',
            priority: 'high',
            align: 'right',
            render: (customer) => (
              <Typography color="error" fontWeight="bold">
                ₱{customer.totalBilledAmount.toFixed(2)}
              </Typography>
            )
          }
        ]}
        data={filteredNoticeData}
        loading={loading}
        loadingMessage="Calculating customer overdue balances..."
        emptyMessage="No customers found matching criteria."
        rowKey="customerid"
      />

      {/* Generate Dialog */}
      <Dialog open={showNoticeDialog} onClose={() => setShowNoticeDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Generate Disconnection Notices</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={3}>
            <Alert severity="info">
                <Typography variant="subtitle2" fontWeight="bold">Summary:</Typography>
                <Typography variant="body2">• Total notices: <strong>{filteredNoticeData.length}</strong></Typography>
                <Typography variant="body2">• Covered months: <strong>{getCoveredMonthsRange()}</strong></Typography>
                <Typography variant="body2">• Disconnection date: <strong>{disconnectionDate ? new Date(disconnectionDate).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' }) : 'Not set'}</strong></Typography>
                <Typography variant="body2">• Reconnection fee: <strong>₱{parseFloat(reconnectionFee || 0).toFixed(2)}</strong></Typography>
            </Alert>
          
            <TextField
              label="Disconnection Date"
              type="date"
              value={disconnectionDate}
              onChange={(e) => setDisconnectionDate(e.target.value)}
              fullWidth
              InputLabelProps={{ shrink: true }}
              required
            />
            
            <TextField
              label="Reconnection Fee"
              type="number"
              value={reconnectionFee}
              onChange={(e) => setReconnectionFee(e.target.value)}
              fullWidth
              InputProps={{
                startAdornment: <InputAdornment position="start">₱</InputAdornment>,
              }}
            />

            <FormControl fullWidth>
              <InputLabel>Signatory</InputLabel>
              <Select
                value={selectedAuthority}
                label="Signatory"
                onChange={(e) => setSelectedAuthority(e.target.value)}
              >
                {users.map((u) => (
                  <MenuItem key={u.userid} value={u.userid}>
                    {u.firstname} {u.lastname} ({u.position})
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <TextField
              label="Warning Message Template"
              multiline
              rows={4}
              value={warningMessage}
              onChange={(e) => setWarningMessage(e.target.value)}
              fullWidth
              helperText="Use {DATE} and {FEE} as placeholders"
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowNoticeDialog(false)}>Cancel</Button>
          <Button 
            variant="contained" 
            startIcon={<PrintIcon />} 
            onClick={() => generateDisconnectionNotices('double')}
            color="error"
          >
            Print 2 per Page
          </Button>
          <Button 
            variant="contained" 
            startIcon={<PrintIcon />} 
            onClick={() => generateDisconnectionNotices('single')}
            color="error"
          >
            Print 1 per Page
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default NoticePage;
